import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import type { SupplyScope } from './execution'

/**
 * Saved entry templates: a site's repeated day, applied in one action.
 *
 * <p>Applying one creates ordinary entries on the report — nothing stays linked to the
 * template afterwards, so every row can be edited or deleted as usual.</p>
 */

export type TemplateModule = 'WORK' | 'LABOUR' | 'MATERIAL'

export interface TemplateLine {
  id: string
  sortOrder: number
  workItemId?: string
  workItemCode?: string
  workItemDescription?: string
  uomCode?: string
  siteLocationId?: string
  labourCategoryId?: string
  labourCategoryName?: string
  contractorId?: string
  contractorName?: string
  workerCount?: number
  workingHours?: number
  overtimeHours?: number
  materialId?: string
  materialCode?: string
  materialName?: string
  supplierId?: string
  supplierName?: string
  supplyScope?: SupplyScope
  defaultQuantity?: number
  rate?: number
  remarks?: string
}

export interface EntryTemplate {
  id: string
  siteId: string
  module: TemplateModule
  name: string
  description?: string
  active: boolean
  useCount: number
  lastUsedAt?: string
  lineCount: number
  summary: string
  version: number
  lines: TemplateLine[]
}

export interface TemplateLineRequest {
  workItemId?: string
  labourCategoryId?: string
  contractorId?: string
  workerCount?: number
  workingHours?: number
  overtimeHours?: number
  materialId?: string
  supplierId?: string
  supplyScope?: SupplyScope
  defaultQuantity?: number | null
  rate?: number | null
  remarks?: string
  sortOrder?: number
}

export interface TemplateSaveRequest {
  siteId: string
  module: TemplateModule
  name: string
  description?: string
  active?: boolean
  lines: TemplateLineRequest[]
}

export interface ApplyResult {
  dailyReportId: string
  applied: number
  skipped: string[]
}

/** Keyed by report rather than site, because that is what the entry screens hold. */
export function useTemplates(module: TemplateModule, params: { siteId?: string; dailyReportId?: string }) {
  const enabled = Boolean(params.siteId || params.dailyReportId)
  return useQuery({
    enabled,
    queryKey: ['entry-templates', module, params.siteId ?? null, params.dailyReportId ?? null],
    queryFn: async () => {
      const { data } = await api.get<EntryTemplate[]>('/entry-templates', {
        params: { module, ...params },
      })
      return data
    },
  })
}

function useTemplateMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entry-templates'] })
    },
  })
}

/** Re-exported so the pickers keep one definition of a BOQ line between them. */
export type { WorkItem as SiteWorkItem } from './workItems'
export { useWorkItems as useSiteWorkItems } from './workItems'

export function useCreateTemplate() {
  return useTemplateMutation(async (body: TemplateSaveRequest) => {
    const { data } = await api.post<EntryTemplate>('/entry-templates', body)
    return data
  })
}

export function useUpdateTemplate() {
  return useTemplateMutation(async ({ id, body }: { id: string; body: TemplateSaveRequest }) => {
    const { data } = await api.put<EntryTemplate>(`/entry-templates/${id}`, body)
    return data
  })
}

export function useDeleteTemplate() {
  return useTemplateMutation(async (id: string) => {
    await api.delete(`/entry-templates/${id}`)
  })
}

/** Saves what is already on the report — how most templates actually get made. */
export function useSaveTemplateFromReport() {
  return useTemplateMutation(
    async (body: {
      dailyReportId: string
      module: TemplateModule
      name: string
      description?: string
      overwrite?: boolean
    }) => {
      const { data } = await api.post<EntryTemplate>('/entry-templates/from-report', body)
      return data
    },
  )
}

/**
 * Applying writes real entries, so every list on the day has to be re-read afterwards.
 */
export function useApplyTemplate(reportId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (templateId: string) => {
      const { data } = await api.post<ApplyResult>(
        `/entry-templates/${templateId}/apply`,
        null,
        { params: { dailyReportId: reportId } },
      )
      return data
    },
    onSuccess: () => {
      for (const key of [
        ['work-progress', reportId],
        ['labour-entries', reportId],
        ['material-receipts', reportId],
        ['work-prefill', reportId],
        ['daily-report', reportId],
        ['completeness', reportId],
        ['entry-templates'],
      ]) {
        void qc.invalidateQueries({ queryKey: key })
      }
    },
  })
}
